(() => {
  if (window.danceflexReviewCarouselReady) return;
  window.danceflexReviewCarouselReady = true;

  const sectionSelector = '.danceflex-reviews';
  const trackSelector = '.danceflex-native-review__item-wrapper';
  const cardSelector = '.danceflex-native-review-item';
  const feedSelector = "[id$='__catalog_store_reviews_feed'] .jdgm-testimonials-container .jdgm-card";
  const cacheKey = 'danceflex-store-reviews-v1';

  let scheduledFrame = null;
  let storeReviews = readCachedReviews();

  function readCachedReviews() {
    try {
      const cachedReviews = JSON.parse(window.sessionStorage.getItem(cacheKey) || '[]');
      if (!Array.isArray(cachedReviews)) return [];
      return cachedReviews.filter(isValidReview);
    } catch (_error) {
      return [];
    }
  }

  function isValidReview(review) {
    return review
      && typeof review.text === 'string'
      && typeof review.reviewer === 'string'
      && Number.isInteger(review.rating)
      && review.rating >= 1
      && review.rating <= 5;
  }

  function saveCachedReviews(reviews) {
    try {
      window.sessionStorage.setItem(cacheKey, JSON.stringify(reviews));
    } catch (_error) {
      // The carousel still works when session storage is unavailable.
    }
  }

  function getAuthoritativeStoreReviews() {
    const feedCards = Array.from(document.querySelectorAll(feedSelector));
    if (!feedCards.length) return [];

    return feedCards.map((feedCard) => ({
      text: feedCard.querySelector('.jdgm-text')?.textContent?.trim() || '',
      reviewer: feedCard.querySelector('.jdgm-reviewer-name')?.textContent?.trim() || '',
      rating: Math.max(1, Math.min(5, feedCard.querySelectorAll('.jdgm-stars .jdgm--on').length || 5)),
    })).filter(isValidReview);
  }

  function getReviewSignature(reviews) {
    return reviews.map((review) => `${review.rating}:${review.reviewer}:${review.text}`).join('|');
  }

  function replaceTemplateValue(template, token, value) {
    return template ? template.split(token).join(String(value)) : String(value);
  }

  function createReviewCard(review) {
    const card = document.createElement('div');
    card.className = 'danceflex-native-review-item jdgm--shop-review';

    const reviewWrapper = document.createElement('div');
    reviewWrapper.className = 'danceflex-native-review-item__review';

    const rating = document.createElement('div');
    rating.className = 'danceflex-native-review-item__review-rating';
    rating.setAttribute('role', 'img');
    rating.setAttribute('aria-label', `${review.rating} stars`);
    rating.textContent = `${'★'.repeat(review.rating)}${'☆'.repeat(5 - review.rating)}`;

    const reviewBody = document.createElement('div');
    reviewBody.className = 'danceflex-native-review-item__review-body';
    const reviewText = document.createElement('p');
    reviewText.textContent = review.text;
    reviewBody.appendChild(reviewText);

    const reviewerWrapper = document.createElement('div');
    reviewerWrapper.className = 'danceflex-native-review-item__reviewer-name-wrapper';
    const reviewerName = document.createElement('div');
    reviewerName.className = 'danceflex-native-review-item__reviewer-name';
    reviewerName.textContent = review.reviewer;
    reviewerWrapper.appendChild(reviewerName);

    reviewWrapper.append(rating, reviewBody);
    card.append(reviewWrapper, reviewerWrapper);
    return card;
  }

  function updateControls(section) {
    const track = section?.querySelector(trackSelector);
    if (!track) return;

    const maximumScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const previousButton = section.querySelector('[data-review-previous]');
    const nextButton = section.querySelector('[data-review-next]');

    if (previousButton) previousButton.disabled = track.scrollLeft <= 2;
    if (nextButton) nextButton.disabled = maximumScroll <= 2 || track.scrollLeft >= maximumScroll - 2;
  }

  function renderStoreReviews(section, reviews) {
    const track = section.querySelector(trackSelector);
    if (!track || !reviews.length) {
      updateControls(section);
      return;
    }

    const signature = getReviewSignature(reviews);
    if (track.dataset.reviewSignature !== signature) {
      track.replaceChildren(...reviews.map(createReviewCard));
      track.dataset.reviewSignature = signature;
      track.scrollLeft = 0;
    }

    const averageRating = reviews.reduce((total, review) => total + review.rating, 0) / reviews.length;
    const formattedRating = averageRating.toFixed(2);
    const ratingElement = section.querySelector('[data-review-rating]');
    const scoreElement = section.querySelector('[data-review-score]');
    const countElement = section.querySelector('[data-review-count]');
    const summaryElement = section.querySelector('[data-review-summary]');

    if (ratingElement) {
      ratingElement.setAttribute(
        'aria-label',
        replaceTemplateValue(section.dataset.ratingLabelTemplate, '__RATING__', formattedRating),
      );
    }
    if (scoreElement && scoreElement.textContent !== formattedRating) {
      scoreElement.textContent = formattedRating;
    }
    if (countElement) {
      const reviewCountText = replaceTemplateValue(
        section.dataset.reviewCountTemplate,
        '__COUNT__',
        reviews.length,
      );
      if (countElement.textContent.trim() !== reviewCountText) {
        countElement.textContent = reviewCountText;
      }
    }
    if (summaryElement) {
      const reviewSummaryText = replaceTemplateValue(
        section.dataset.reviewSummaryTemplate,
        '__COUNT__',
        reviews.length,
      );
      if (summaryElement.textContent.trim() !== reviewSummaryText) {
        summaryElement.textContent = reviewSummaryText;
      }
    }

    section.dataset.reviewState = 'ready';
    window.requestAnimationFrame(() => updateControls(section));
  }

  function hydrateReviewSections() {
    const authoritativeReviews = getAuthoritativeStoreReviews();
    if (authoritativeReviews.length) {
      const currentSignature = getReviewSignature(storeReviews);
      const authoritativeSignature = getReviewSignature(authoritativeReviews);
      if (currentSignature !== authoritativeSignature) {
        storeReviews = authoritativeReviews;
        saveCachedReviews(storeReviews);
      }
    }

    document.querySelectorAll(sectionSelector).forEach((section) => {
      renderStoreReviews(section, storeReviews);
    });
  }

  function scheduleHydration() {
    if (scheduledFrame !== null) return;
    scheduledFrame = window.requestAnimationFrame(() => {
      scheduledFrame = null;
      hydrateReviewSections();
    });
  }

  function scrollCarousel(section, direction) {
    const track = section.querySelector(trackSelector);
    const card = track?.querySelector(cardSelector);
    if (!track || !card) return;

    const gap = parseFloat(window.getComputedStyle(track).gap) || 0;
    const step = card.getBoundingClientRect().width + gap;
    const maximumScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const target = Math.min(maximumScroll, Math.max(0, track.scrollLeft + direction * step));

    track.scrollTo({ left: target, behavior: 'smooth' });
  }

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element
      ? event.target.closest('[data-review-previous], [data-review-next]')
      : null;
    const section = button?.closest(sectionSelector);
    if (!button || !section) return;

    event.preventDefault();
    scrollCarousel(section, button.hasAttribute('data-review-next') ? 1 : -1);
  });

  document.addEventListener('scroll', (event) => {
    if (!(event.target instanceof Element) || !event.target.matches(trackSelector)) return;
    updateControls(event.target.closest(sectionSelector));
  }, true);

  document.addEventListener('shopify:section:load', scheduleHydration);
  window.addEventListener('pushstate', scheduleHydration);
  window.addEventListener('popstate', scheduleHydration);
  window.addEventListener('pageshow', scheduleHydration);
  window.addEventListener('resize', scheduleHydration);

  const observer = new MutationObserver(scheduleHydration);
  observer.observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleHydration, { once: true });
  } else {
    scheduleHydration();
  }
})();
